import axios from "axios";
import { commonAuthHeaders } from "../auth/utils";
import Logger from "../logger";

export const API_URL = `${process.env.REACT_APP_APIS_URL}/apis`;

const instance = axios.create({
  baseURL: API_URL,
  timeout: 3600000,
  withCredentials: true
});

//NOTE: deprecated above v.20
export const getAxiosCancelTokenSource = () => {
  const CancelToken = axios.CancelToken;
  const source = CancelToken.source();
  return source
}

export const isCanceledRequestError = (error) => {
  return axios.isCancel(error)
}

instance.interceptors.request.use(
  async (config) => {
    config.headers = await commonAuthHeaders()
    return config;
  },
  (error) => {
    Logger.error('axios request error', { error });
    // Do something with request error
    return Promise.reject(error);
  }
);

export const getAutodeskToken = async () => {
  return instance.get("/bim/token");
};

export const uploadFile = async ({ data }, { onUploadProgress, headers }) => {
  const res = await instance.post("/bim/upload-file", data, {
    onUploadProgress,
    headers,
  });
  return res;
};

export const createProject = async (
  { data },
  { onUploadProgress, headers, cancelToken }
) => {
  const res = await instance.post("/bim/create-project", data, {
    onUploadProgress,
    headers,
    cancelToken,
  });
  return res;
};

export const uploadProjectFile = async (
  { data },
  { onUploadProgress, headers }
) => {
  const res = await instance.post("/bim/project/upload", data, {
    onUploadProgress,
    headers,
  });
  return res;
};

export const generateBOM = async (projectId, viewId) => {
  const res = await instance.post(
    `/bom/project/generate-bom/${projectId}${viewId ? `?viewId=${viewId}` : ""}`
  );
  return res;
};

export const getProjectManifest = async ({ urn, region, houseId }) => {
  const result = await instance.get(`/bim/get/manifest/${houseId}/${urn}`, {
    params: {
      region,
    },
  });
  return result;
};

export const getDownloadUrl = (key, params = {}) => {
  const qs = new URLSearchParams(params);
  return `${API_URL}/custom/product/download?key=${key}&${qs.toString()}`;
};

export const downloadFileWithoutInstance = async (file) => {
    if (!file) throw new Error("No file to download");

    // Plain string URL — open directly
    if (typeof file === "string") {
        window.open(file, "_blank", "noopener,noreferrer");
        return;
    }

    // S3 object { Bucket, Key } — fetch a pre-signed URL first
    const { data } = await getDocDownloadUrlAPI(file);
    const url = data?.url || data;
    if (!url) throw new Error("Failed to get download URL");
    window.open(url, "_blank", "noopener,noreferrer");
};

// Common function for handling file downloads
const downloadFileFromResponse = (data, fileName, contentType) => {
  const downloadUrl = window.URL.createObjectURL(new Blob([data], contentType ? { type: contentType } : undefined));
  const link = document.createElement('a');
  link.href = downloadUrl;
  link.setAttribute('download', fileName);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(downloadUrl);
};

// Extract filename from Content-Disposition header
const getFileNameFromContentDisposition = (headers) => {
  const disposition = headers?.['content-disposition'] || '';
  const match = disposition.match(/filename\*?=['"]?(?:UTF-\d['"]*)?([^;\r\n"']*)['"]?/i);
  return match?.[1]?.trim() || null;
};

// Generic file download function using instance
const downloadFileWithInstance = async (url, fileName, options = {}) => {
  try {
    const { data, headers } = await instance.request({
      url,
      method: 'GET',
      responseType: 'blob',
      ...options
    });
    const contentType = headers?.['content-type'] || '';
    // A JSON body means the route answered an error, not a file.
    if (contentType.includes('application/json')) {
      Logger.error('download returned JSON', { context: { url, contentType } });
      return false;
    }
    const resolvedName = fileName || getFileNameFromContentDisposition(headers) || 'download';
    downloadFileFromResponse(data, resolvedName, contentType);
    return true;
  } catch (error) {
    Logger.error('download error', { error, context: { url, fileName } });
    return false;
  }
};

export const downloadFile = async (key, params) => {
  return downloadFileWithInstance(
    getDownloadUrl(key, params),
    getFileNameFromAwsKey(key)
  );
};

export const getFileNameFromAwsKey = (key) => key ? key.split("/").slice(-1)[0] : key;

export const downloadMultipleFiles = async (keys, params) => {
  const validKeys = keys.filter(Boolean);
  validKeys.forEach(key => downloadFile(key, params));
};

// Excel export functions using common download
export const bomExcelExport = (projectId, detailed = false) => {
  const fileName = `${projectId}-${detailed ? "detailed" : "lean"}.xlsx`;
  const url = `${API_URL}/bom/project/excel/${projectId}${detailed ? "/detailed" : ""}`;
  return downloadFileWithInstance(url, fileName);
};

export const privateCatalogCsvExport = () => {
  return downloadFileWithInstance(
    `${API_URL}/cms/export/catalog/csv`
  );
};

export const privateCatalogTemplateCsvExport = () => {
  return downloadFileWithInstance(
    `${API_URL}/cms/export/catalog/csv-template`
  );
};

export const privateCatalogProductExcelExport = (productId) => {
  return downloadFileWithInstance(
    `${API_URL}/cms/export/product/${productId}/excel`,
    `${productId}.xlsx`
  );
};

export const sandboxExcelExport = (projectId, viewId) => {
  return downloadFileWithInstance(
    `${API_URL}/house/${projectId}/${viewId}/materials`,
    `${projectId}.xlsx`
  );
};

export const getGeoCordinates = () => {
  return instance.get(`${API_URL}/custom/location`);
};

export const fetchBomData = async (id, viewId, objectId = "ROOT") => {
  const { data } = await instance.get(`/house/${id}/bom`, {
    params: {
      viewId,
      objectId,
    },
  });
  return data;
};

export const fetchBomObjectData = async (id, viewId, objectId = "ROOT") => {
  const { data } = await instance.get(`/house/${id}/bom/object`, {
    params: {
      viewId,
      objectId,
    },
  });
  return data;
};

export const fetchBomObjectDataByExternalId = async (id, externalId) => {
  const { data } = await instance.get(`/house/${id}/bom/object-by-external-id`, {
    params: {
      externalId
    },
  });
  return data;
};

// returns the updated BOM object, same shape as fetchBomObjectData
export const updateBomObjectProperties = async (id, viewId, objectId, properties) => {
  const { data } = await instance.patch(
    `/houses/${id}/views/${viewId}/bom-objects/${objectId}/properties`,
    { properties }
  );
  return data;
};

// adds new properties (an existing displaySection/label is a 409), returns the updated BOM object
export const addBomObjectProperties = async (id, viewId, objectId, properties) => {
  const { data } = await instance.post(
    `/houses/${id}/views/${viewId}/bom-objects/${objectId}/properties`,
    { properties }
  );
  return data;
};

export const openPdf = async (file = {}, { pageNumber, type, id } = {}) => {
  try {
    const { data } = await instance.request({
      url: `/custom/pdf-viewer`,
      method: 'GET',
      responseType: 'blob',
      params: {
        bucketName: file.Bucket,
        key: file.Key,
        ...(type ? { type } : {}),
        ...(id ? { id } : {}),
      }
    });

    const blobUrl = window.URL.createObjectURL(new Blob([data], { type: 'application/pdf' }));
    let url = blobUrl;
    if (pageNumber) {
      url += `#page=${pageNumber}`;
    }
    window.open(url, '_blank');
    window.URL.revokeObjectURL(blobUrl);
  } catch (error) {
    Logger.error('download error', { error });
  }
};

export const getPdfFileContent = (file = {}, { type, id } = {}) => new Promise(async (resolve, reject) => {
  try {
    const { data } = await instance.request({
      url: `/custom/pdf-viewer`,
      method: 'GET',
      responseType: 'blob',
      params: {
        bucketName: file.Bucket,
        key: file.Key,
        ...(type ? { type } : {}),
        ...(id ? { id } : {}),
      }
    });
    const reader = new FileReader();
    reader.readAsArrayBuffer(data);
    reader.onloadend = () => {
      resolve(reader.result);
    };
    reader.onerror = () => {
      reject(new Error("Failed to read PDF blob."));
    };
  } catch (error) {
    Logger.error('download error', { error });
    reject(error);
  }
})


// ---- authentication

export const logoutAPI = async () => {
  return await instance.post("/auth/logout")
}

export const loginAPI = async (loginData) => {
  return await instance.post("/auth/firebase/login", loginData)
}

export const firebaseTokenAPI = async (token) => {
  return await instance.post("/auth/firebase/callback", { idToken: token })
}

export const signupAPI = async (signupData) => {
  return await instance.post("/auth/firebase/signup", signupData)
}

export const sendVerificationEmailAPI = async () => {
  return await instance.post("/auth/send-verification-email")
}

export const sendEmailOtpAPI =  async () => {
  return await instance.post("/auth/send-email-otp")
}

export const verifyEmailOtpAPI =  async (otpData) => {
  return await instance.post("/auth/verify-email-otp", otpData)
}


// --- Docs

export const getDocsAPI = async (docsData) => {
  return await instance.get("/docs", { params : docsData })
}

export const searchDocsAPI = async (searchData, cancelToken) => {
  return await instance.post("/docs/search", searchData, { cancelToken })
}

export const uploadDocsAPI = async (formData) => {
  return await instance.post("/docs/upload", formData, {
    headers: { "Content-Type": "multipart/form-data" },
  })
}

export const updateDocAPI = async (docId, patch) => {
  return await instance.patch(`/docs/${docId}`, patch)
}

// selection: { docIds } or { selectAll: true, filters, excludeIds, expectedCount }
export const deleteDocsAPI = async (selection) => {
  return await instance.delete("/docs", { data: selection })
}

// action: "add" | "remove" | "replace" | "remove-all"
export const updateDocsTagsAPI = async ({ action, valueIds, ...selection }) => {
  return await instance.patch("/docs", { ...selection, action, valueIds })
}

// GET /docs/export → the list plus the user guidelines, as one ZIP named by the
// backend's Content-Disposition. Takes the same params as /docs/search.
export const exportDocsAPI = async (params = {}) => {
  const search = new URLSearchParams()
  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === null || value === "") return
    if (Array.isArray(value)) value.forEach((item) => search.append(key, item))
    else search.append(key, value)
  })
  const qs = search.toString()
  return await downloadFileWithInstance(`/docs/export${qs ? `?${qs}` : ""}`)
}

// --- Bulk edit by CSV: the exported docs list, corrected and re-uploaded.
// POST answers 202 with a queued job; poll the one below until it finishes.
export const importDocsCsvAPI = async (formData) => {
  return await instance.post("/docs/imports", formData, {
    headers: { "Content-Type": "multipart/form-data" },
  })
}

export const getDocsImportJobAPI = async (jobId) => {
  return await instance.get(`/docs/imports/${jobId}`)
}

// --- Docs taxonomy ("Manage filters and attributes" panel)
// A unified group holds values and is typed `filter` | `attribute`. Filter
// groups drive search/facets; attribute groups are doc metadata (display-only).

// GET /docs/groups → { items: [{ ...group, type, values: [...] }] }
export const getDocGroupsAPI = async () => {
  return await instance.get("/docs/groups")
}

export const getDocsFiltersAPI = async (selection) => {
  return await instance.post("/docs/filters", selection)
}

// POST /docs/groups/apply → batch taxonomy changes (one transactional call).
export const applyDocGroupsAPI = async ({ actions, requestId }) => {
  return await instance.post("/docs/groups/apply", { actions, requestId })
}

export const askDocsAPI = async (askDocsData, cancelToken) => {
  return await instance.post('/docs/ask', askDocsData, { cancelToken })
}

// --- Data-sets
// A data-set is an organization CSV of code/description levels, parsed into a tree on upload.

// GET /data-sets → { items: [...] }, newest first
export const getDataSetsAPI = async () => {
  return await instance.get("/data-sets")
}

// GET /data-sets/:id/tree → { items: [{ id, code, description, displayName, subDisplayName, children }] }
export const getDataSetTreeAPI = async (dataSetId) => {
  return await instance.get(`/data-sets/${dataSetId}/tree`)
}

export const createDataSetAPI = async (formData) => {
  return await instance.post("/data-sets", formData, {
    headers: { "Content-Type": "multipart/form-data" },
  })
}

export const uploadDataSetVersionAPI = async (dataSetId, formData) => {
  return await instance.put(`/data-sets/${dataSetId}/file`, formData, {
    headers: { "Content-Type": "multipart/form-data" },
  })
}

export const deleteDataSetAPI = async (dataSetId) => {
  return await instance.delete(`/data-sets/${dataSetId}`)
}

export const downloadDataSetFileAPI = async (dataSetId) => {
  return await downloadFileWithInstance(`/data-sets/${dataSetId}/file`)
}

// The CSV template plus its user guidelines, as one ZIP.
export const downloadDataSetTemplateAPI = async () => {
  return await downloadFileWithInstance("/data-sets/template")
}

// Fetch-based (axios cannot consume a streaming response body in the browser).
// Relays the backend's `text/event-stream` frames from POST /docs/ask/stream:
// `event: token` frames carry answer chunks, `event: error` a mid-stream
// failure, and `:` comment lines are heartbeats to be ignored.
export const askDocsStreamAPI = async (askDocsData, { signal, onEvent } = {}) => {
  const headers = await commonAuthHeaders({
    'Content-Type': 'application/json',
    'Accept': 'text/event-stream'
  })

  const response = await fetch(`${API_URL}/docs/ask/stream`, {
    method: 'POST',
    headers,
    credentials: 'include',
    body: JSON.stringify(askDocsData),
    signal
  })

  if (!response.ok || !response.body) {
    let errorBody = null
    try {
      errorBody = await response.json()
    } catch (parseError) {
      // non-JSON error body, keep the status-based message
    }
    const error = new Error(errorBody?.error?.message || `AI stream request failed with status ${response.status}`)
    error.code = errorBody?.error?.code
    error.status = response.status
    throw error
  }

  const dispatchFrame = (rawFrame) => {
    let eventName = 'message'
    const dataLines = []

    rawFrame.split(/\r?\n/).forEach(line => {
      if (!line || line.startsWith(':')) return // SSE comment (heartbeat)
      const separatorIndex = line.indexOf(':')
      const field = separatorIndex === -1 ? line : line.slice(0, separatorIndex)
      let value = separatorIndex === -1 ? '' : line.slice(separatorIndex + 1)
      if (value.startsWith(' ')) value = value.slice(1)

      if (field === 'event') {
        eventName = value
      } else if (field === 'data') {
        dataLines.push(value)
      }
    })

    if (eventName === 'message' && !dataLines.length) return // heartbeat-only frame
    onEvent && onEvent({ event: eventName, data: dataLines.join('\n') })
  }

  const reader = response.body.getReader()
  const decoder = new TextDecoder('utf-8')
  let buffer = ''

  try {
    while (true) {
      const { done, value } = await reader.read()
      if (done) break

      buffer += decoder.decode(value, { stream: true })

      // frames are separated by a blank line
      let boundaryMatch
      while ((boundaryMatch = buffer.match(/\r?\n\r?\n/))) {
        const frame = buffer.slice(0, boundaryMatch.index)
        buffer = buffer.slice(boundaryMatch.index + boundaryMatch[0].length)
        if (frame.trim()) dispatchFrame(frame)
      }
    }

    if (buffer.trim()) dispatchFrame(buffer)
  } finally {
    reader.releaseLock()
  }
}

export const isAbortError = (error) => {
  return error?.name === 'AbortError' || axios.isCancel(error)
}

export const getPastChatsAPI = async (pastChatsData, cancelToken) => {
  return await instance.post('/docs/chats', pastChatsData, { cancelToken })
}

export const getChatHistoryAPI = async (chatHistoryData, cancelToken) => {
  return await instance.post('/docs/chat/history', chatHistoryData, { cancelToken })
}

export const getBrandsAPI = async ({ cancelToken } = {}) => {
  const { data } = await instance.get("/brands", { cancelToken });
  return data?.items || [];
};

export const getCategoriesAPI = async ({ cancelToken } = {}) => {
  const { data } = await instance.get("/categories", { cancelToken });
  return data?.items || [];
};

export const getDistributorsAPI = async ({ cancelToken } = {}) => {
  const { data } = await instance.get("/distributors", { cancelToken });
  return data?.items || [];
};

// The legacy GraphQL createDistributor mutation accepted an array; REST is
// one resource per call. Callers that need to create many wrap this in
// Promise.all.
export const createDistributorAPI = async (input) => {
  const { data } = await instance.post("/distributors", input);
  return data;
};

export const getAnalyticsEventsAPI = async (params, { cancelToken } = {}) => {
  // Strip null/undefined/empty values so we don't send `?to=undefined`.
  const cleaned = Object.fromEntries(
    Object.entries(params || {}).filter(
      ([, v]) => v !== undefined && v !== null && v !== ""
    )
  );
  const { data } = await instance.get("/analytics/events", {
    params: cleaned,
    cancelToken,
  });
  return data?.items || [];
};

export const deleteChatHistoryAPI = async (chatHistoryData) => {
  return await instance.delete('/docs/chat/history', { data: chatHistoryData } )
}

export const getDocDownloadUrlAPI = async (file, expiresIn = 900) => {
  return await instance.get("/docs/download-url", { params: { bucketName: file?.Bucket, key: file?.Key, expiresIn } });
};

export const getAiProcessingRunsAPI = async (params, cancelToken) => {
  return await instance.get("/ai-processing/runs", { params, cancelToken });
};

// Another attempt at the same run, rather than a fresh one.
export const retryAiProcessingRunAPI = async (runId) => {
  return await instance.post(`/ai-processing/runs/${runId}/attempts`)
}

// Ends the run and deletes the documents it was ingesting.
export const cancelAiProcessingRunAPI = async (runId) => {
  return await instance.post(`/ai-processing/runs/${runId}/actions/cancel`)
}

export const startAiProcessingRunAPI = async (data) => {
  return await instance.post("/ai-processing/runs", data);
};

// Stable, auth-checked embeddable link to a doc's file (302 → presigned URL).
// Works as an <a href>/<iframe src> because the API instance uses cookie auth.
export const getDocFileUrl = (docId) => `${API_URL}/docs/${docId}/file`;

// --- Annexes (child docs)

// Attach existing doc(s) as annexes of a parent.
export const attachAnnexesAPI = async ({ parentId, annexDocIds }) => {
  return await instance.post(`/docs/${parentId}/annexes`, { annexDocIds });
};

// Detach a single annex from its parent (becomes a top-level doc again).
export const detachAnnexAPI = async ({ parentId, annexId }) => {
  return await instance.delete(`/docs/${parentId}/annexes/${annexId}`);
};

// --- ACC (Autodesk Construction Cloud) import

export const getAccHubsAPI = async () => {
  return await instance.get("/docs/acc/hubs");
};

export const getAccProjectsAPI = async (hubId) => {
  return await instance.get("/docs/acc/projects", { params: { hubId } });
};

// fileType: "docs" (pdf/word/excel/… — docs portal default) or "project" (BIM files).
export const getAccContentsAPI = async ({ hubId, projectId, folderId, fileType = "docs" }) => {
  return await instance.get("/docs/acc/contents", { params: { hubId, projectId, folderId, fileType } });
};

// Picked files (`items`) and whole folders (`folders`) are copied by a queued
// job: POST answers 202 with it; poll the one below until it finishes.
export const importAccDocsAPI = async (payload) => {
  return await instance.post("/docs/acc/import", payload);
};

export const getAccImportJobAPI = async (jobId) => {
  return await instance.get(`/docs/acc/imports/${jobId}`);
};

// Re-checks every doc imported from ACC (Forma) against its ACC item: new
// versions and renames replace the file in place. No body - it is org-wide.
// POST answers 202 with the job, or 409 with `details.jobId` while one runs.
export const syncAccDocsAPI = async () => {
  return await instance.post("/docs/acc/sync");
};

export const getAccSyncJobAPI = async (jobId) => {
  return await instance.get(`/docs/acc/syncs/${jobId}`);
};
